<template>
  <div class="upcoming-sessions-report">
    <div class="col-12 text-right">
      <form-component>
        <div class="form-row">
          <input-text
            id="search"
            v-model="search"
            :value="search"
            name="search"
            label="search"
            @input="search=$event;delay('filter-outcomes', resetToPageZero, 500);"
          />
          <input-select
            id="canceled"
            :value="canceled"
            name="canceled"
            label="Canceled"
            type="select"
            :options="[{id: null, display_label: ''}, {id: true, display_label: 'Yes'}, {id: false, display_label: 'No'}, ]"
            @input="canceled=$event;resetToPageZero();"
          />
          <input-datepicker
            id="start_time_after"
            :value="from"
            :disabled-dates="notBeforeToday"
            name="start_time_after"
            label="From"
            @input="setFrom($event);resetToPageZero();"
          />
          <input-datepicker
            id="start_time_before"
            :value="through"
            :disabled-dates="notBeforeFrom"
            name="start_time_before"
            label="Through"
            @input="setThrough($event);resetToPageZero();"
          />
          <b-tooltip
            v-if="!from || !through || fetching"
            target="upcomingSessionsReportExportButtonDiv"
          >
            Must select from and through dates to export.
          </b-tooltip>
          <div id="upcomingSessionsReportExportButtonDiv" class="btn-export">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!from || !through || fetching"
              @click="exportReport"
            >
              Export
            </button>
          </div>
        </div>
      </form-component>
    </div>
    <b-table
      no-local-sorting 
      striped 
      :fields="sessionFields" 
      :items="upcomingSessions" 
      head-variant="dark"
      @sort-changed="sortChanged">
      <template #cell(canceled)="row">
        <div v-if="row.item.canceled" v-b-tooltip.hover.top="'Session is Canceled'" class="canceled-bar">
        </div>
      </template>
      <template #cell(counselor)="row">
        <a :href="`/counselor/profile/${row.item.counselor.id}`">
          {{ row.item.counselor.firstName }} {{ row.item.counselor.lastName }}
        </a>
      </template>
      <template #cell(client)="row">
        <a :href="`/student/${row.item.client.userId}`">
          {{ row.item.client.firstName }} {{ row.item.client.lastName }}
        </a>
      </template>
      <template #cell(campus)="row">
        {{ row.item.campus.name }}
      </template>
      <template #cell(start)="row">
        {{ row.item.start | withTimeZone | format('M/D/YYYY h:mmA (z)') }}
      </template>
      <template #cell(payment)="row">
        {{ row.item.payment.status }}
        <template v-if="row.item.payment.status === 'Paid'">
          <template v-if="row.item.payment.type === 'CampusCredit'">
            (Credit)
          </template>
        </template>
      </template>
      <template #cell(show_details)="row">
        <div v-if="!row.item._showDetails">
          <button v-if="row.item.payment.status === 'Paid'" class="btn btn-ghost" type="button" @click="toggleRefundPaymentConfirm(row)">
            Refund Payment
          </button>
          <button v-else-if="row.item.payment.status === 'Pending'" class="btn btn-ghost" type="button" @click="toggleForgivePaymentConfirm(row)">
            Forgive Payment
          </button>
          <button v-if="!row.item.canceled" class="btn btn-ghost" type="button" @click="toggleCancelSessionConfirm(row)">
            Cancel Session
          </button>
        </div>
      </template>
      <template #row-details="row">
        <Loader v-if="acting" style="width: 100%;" />
        <div v-else class="row-details-container">
          <div class="message">
            {{ row.item.message }}
          </div>
          <div class="buttons">
            <button class="btn btn-warn" type="button" @click="row.item.action(row.item)">
              Confirm
            </button>
            <button class="btn btn-ghost" type="button" @click="row.toggleDetails()">
              Back
            </button>
          </div>
        </div>
      </template>
    </b-table>
    <Loader v-if="fetching" style="width: 100%;" />
    <div v-else-if="!upcomingSessions.length" class="text-center">
      No upcoming sessions found.
    </div>
    <div v-else-if="!pageToken" class="text-center">
      All records loaded.
    </div>
    <button
      v-else
      type="button"
      class="btn btn-primary"
      @click="fetchNextRecords()"
    >
      Load More
    </button>
  </div>
</template>

<script>
import Loader from '@/components/Loader.vue';
import SessionClient from '@/api/SessionClient';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputText from '@/components/inputs/InputText.vue';
import InputDatepicker from '@/components/inputs/InputDatepicker.vue';
import FormComponent from '@/components/FormComponent.vue';
import downloadCsv from '@/helpers/mixins/download-csv.js'
import delay from '@/helpers/mixins/delay.js';
import urlQueryEncoder from '@/helpers/mixins/url-query-encoder.js';
import moment from 'moment';

export default {
    name: 'UpcomingSessionsReport',
    components:{
      Loader,
      InputSelect,
      InputDatepicker,
      InputText,
      FormComponent,
    },
    mixins:[downloadCsv, delay, urlQueryEncoder],
    data() {
      return {
        sessionsClient: new SessionClient(this.$http),
        sessionFields: [
          { key: 'canceled', label: '' },
          { key: 'counselor', label: 'Counselor', sortable: true, sortAsc: 'ListSessionsByCounselorNameAscending', sortDesc: 'ListSessionsByCounselorNameDescending', },
          { key: 'client', label: 'Client', sortable: true, sortAsc: 'ListSessionsByClientNameAscending', sortDesc: 'ListSessionsByClientNameDescending', },
          { key: 'campus', label: 'Campus', sortable: true, sortAsc: 'ListSessionsByCampusNameAscending', sortDesc: 'ListSessionsByCampusNameDescending', },
          { key: 'start', label: 'Start', sortable: true, sortAsc: 'ListSessionsByStartTimeAscending', sortDesc: 'ListSessionsByStartTimeDescending', },
          { key: 'payment', label: 'Payment', sortable: true, sortAsc: 'ListSessionsByPaymentStatusAscending', sortDesc: 'ListSessionsByPaymentStatusDescending', },
          { key: 'show_details', label: '' }
        ],

        search: null,
        from: null,
        through: null,
        canceled: null,
        page: 0,
        customSort: null,

        notBeforeToday: {
          to: moment().startOf('day').toDate(),
        },

        acting: false,
        fetching: false,
        pageToken: 'ListSessionsByStartTimeAscending',
        upcomingSessions: [],
      }
    },
    computed: {
      notBeforeFrom() {
        if (this.from) return { to:moment(this.from).startOf('day').toDate() }
        return { to:moment().startOf('day').toDate() }
      },
    },
    destroyed() {
      removeEventListener('scroll', (event) => this.trackScroll(event));
    },
    async mounted() {
      this.loadSortsAndFiltersFromQuery();
      
      await this.resetToCurrentPage();

      window.scrollTo(0, this.scroll);
      addEventListener('scroll', (event) => this.trackScroll(event));
    },
    methods: {
      loadSortsAndFiltersFromQuery() {
        const SuperAdminAppointments = this.getUrlQuery('SuperAdminAppointments');
        if (!SuperAdminAppointments) 
          return;

        const { 
          from, 
          through,
          canceled, 
          search, 
          page, 
          scroll, 
          customSort 
        } = SuperAdminAppointments;
        
        this.setFrom(from);
        this.setThrough(through);
        
        if (search)
          this.search = search;

        if (scroll)
          this.scroll = Number(scroll);

        if (canceled)
          this.canceled = canceled === 'Yes' ? 'true'
            : canceled === 'No' ? 'false'
            : null;

        if (page)
          this.page = Number(page);
        
        if (customSort && this.sessionFields.find(x => x.sortAsc === customSort || x.sortDesc === customSort))
          this.customSort = customSort;
      },
      updateQuery() {
        this.updateUrlQuery('SuperAdminAppointments', {
          from: this.from || undefined,
          through: this.through || undefined,
          canceled: this.canceled === 'true' ? 'Yes' 
            : this.canceled === 'false' ? 'No' 
            : undefined,
          search:this.search || undefined,
          page: this.page || undefined,
          scroll: this.scroll || undefined,
          customSort: this.customSort || undefined,
        });
      },

      resetToPageZero(){
        return this.reset(0, this.customSort);
      },
      resetToCurrentPage() {
        return this.reset(this.page, this.customSort);
      },
      reset(targetPage, pageToken) {
        if(targetPage == null || targetPage == undefined)
          targetPage = 0;
        if(!pageToken)
          pageToken = 'ListSessionsByStartTimeAscending'

        this.pageToken = pageToken;
        this.page = 0;
        this.upcomingSessions = [];

        return this.fetchNextRecords(targetPage);
      },

      setFrom(from) {
        if (!from || moment(from).isValid()) { this.from = from; }

        if (moment(from) < moment()) { this.from = moment().toISOString(); }

        if (this.through && from > this.through) { this.through = this.from; }
      },
      setThrough(through) {
        if (!through || moment(through).isValid()) { this.through = through; }
      },
      trackScroll() {
          this.scroll = window.scrollY;
          this.updateQuery();
      },
      sortChanged(change) {
        const { sortBy, sortDesc } = change;
        const column = this.sessionFields.find(x => x.key === sortBy);
        if(!column) {
          console.error('Could not find sorting column.')
          return;
        }
        this.customSort = sortDesc ? column.sortDesc : column.sortAsc;

        return this.resetToPageZero();
      },

      toggleRefundPaymentConfirm(row) {
          row.item.action = this.refundPayment.bind(this);
          row.item.message = 'Are you sure you want to refund this session\'s payment?';
          row.toggleDetails();
      },
      toggleForgivePaymentConfirm(row) {
          row.item.action = this.refundPayment.bind(this);
          row.item.message = 'Are you sure you want to cancel this session\'s payment?';
          row.toggleDetails();
      },
      toggleCancelSessionConfirm(row) {
          row.item.action = this.cancelSession.bind(this);
          row.item.message = 'Are you sure you want to cancel this session?';
          row.toggleDetails();
      },

      async fetchNextRecords(targetPage = 0) {
        try {
          this.fetching = true;

          do {
            const response = await this.fetch(this.pageToken, 20);

            this.page++;
            this.pageToken = response.nextPageToken;

            response.sessions.forEach(outcome => {
              this.upcomingSessions.push({
                ...outcome,
                start: moment(outcome.start),
                end: moment(outcome.end),
              });
            });
          } while (this.page < targetPage && this.pageToken)

          this.updateQuery();
        } catch (ex) {
          console.error(ex);
        } finally {
          this.fetching = false;
        }
      },
      async fetch(pageToken, recordCount) {
          let search;
          if (this.search) {
              search = this.search
                  .split(/\s+/) // match all whitespace
                  .filter(Boolean) // remove empty
          }

          let fromDate;
          if (this.from) {
              let from = moment(this.from).startOf('day');
              if (from < moment()) { from = moment(); }
              fromDate = from.toISOString();
          } else {
              fromDate = moment().toISOString()
          }

          let canceled;
          if (this.canceled !== null) {
              canceled = this.canceled;
          }

          let throughDate = null;
          if (this.through) { throughDate = moment(this.through).endOf('day').toISOString() }

          return await this.sessionsClient.getSessionsForAdmin(
              fromDate,
              throughDate,
              search,
              canceled,
              recordCount,
              pageToken);
      },

      async refundPayment(session) {
        try {
          this.acting = true;
          const res = await this.sessionsClient.admin_refund_session(session.id)
          this.$store.commit('setGlobalAlert', {
            msg: 'Payment Forgiven.',
          });
        } catch (ex) {
          console.error(ex);
          this.$store.commit('setGlobalAlert', {
              msg: 'Failed to forgive session. Please try again!', error: true,
          });
        } finally {
          this.acting = false;
        }
        this.resetToCurrentPage();
      },
      async cancelSession(session) {
        try {
          this.acting = true;
          const res = await this.sessionsClient.admin_cancel_session(session.id)
          this.$store.commit('setGlobalAlert', {
            msg: 'Session Canceled.',
          });
        } catch (ex) {
          console.error(ex);
          this.$store.commit('setGlobalAlert', {
            msg: 'Failed to cancel session. Please try again!', error: true,
          });
        } finally {
          this.acting = false;
        }
        this.resetToCurrentPage(this.page);
      },
      async exportReport() {
        try {
          this.fetching = true;

          let pageToken = 'ListSessionsByStartTimeAscending';
          const records = [];
          do {
            const response = await this.fetch(pageToken, 50);

            pageToken = response.nextPageToken;

            records.push(...response.sessions);
          } while (pageToken)

          const csv = this.toCsv(records, [
            { header: 'Counseler', mapper: (x) => `${x.counselor.firstName} ${x.counselor.lastName}` },
            { header: 'Client', mapper: (x) => `${x.client.firstName} ${x.client.lastName}` },
            { header: 'Campus', mapper: (x) => x.campus.name },
            { header: 'SessionStart', mapper: (x) => x.start },
            { header: 'PaymentStatus', mapper: (x) => x.payment.status },
            { header: 'PaymentType', mapper: (x) => x.payment.type },
            { header: 'Canceled', mapper: (x) => (x.canceled ? 'Yes' : 'No') },
          ])

          this.downloadCsv(csv, `upcoming_session_${moment().valueOf()}.csv`);
        } catch (ex) {
          console.error(ex);
        } finally {
          this.fetching = false;
        }
      },
    }
}
</script>

<style lang="scss">
.upcoming-sessions-report {
    .form__input {
        margin-right: 10px;
    }
    div.btn-export {
        align-self: center;
    }
    .b-table {
        color: $white;
        .thead-dark th{
            background-color: $dark-blue !important;
            border-color: $border-color !important;
        }

        .canceled-bar {
            border-radius: 5px;
            width: 5px;
            height: 25px;
            background-color: $red;
        }
        .row-details-container {
            margin-bottom: 25px;

            div.message {
                margin-top: 25px;
                margin-bottom: 25px;
            }
        }
        button.btn {
            margin-right: 5px;
        }
        button.btn-warn {
            color: $white;
            background-color: $red;
        }
    }
}
</style>
